import React, { AriaAttributes, useEffect, useRef, useState, useId } from 'react';
import classnames from 'classnames';
import { Props as IconProps, IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import NetworkBarItem from '../NetworkBarItem';
import ArrowRight from '@wix/da-ds/pkg/Icons/ArrowRight';
import ArrowDown from '@wix/da-ds/pkg/Icons/ArrowDown';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import s from './NetworkBarItemGroup.scss';

export interface Props {
  title?: string;
  icon: React.ComponentType<IconProps>;
  isCollapsed?: boolean;
  isFolded?: boolean;
  hasNewContent?: boolean;
  withFolding?: boolean;
  className?: string;
  children?: React.ReactNode;
  biData?: BiEvent;
  toggleAriaLabel?: AriaAttributes['aria-label'];
  onFoldToggle?: () => void;
}

const NetworkBarItemGroup: React.FC<Props> = ({
  children,
  className,
  icon: Icon,
  isCollapsed,
  title,
  withFolding,
  hasNewContent,
  isFolded,
  biData,
  onFoldToggle,
  toggleAriaLabel,
}) => {
  const toggleId = useId();
  const foldableId = useId();

  const foldableRef = useRef<HTMLDivElement>(null);
  const [foldableHeight, setFoldableHeight] = useState(0);
  // Set the height using useEffect so we can calc it _after_ this component's children render,
  // otherwise, offsetHeight won't include the rendered show more button in FoldableGroupList.
  // (We include isCollapsed and children to fire this when the menu is first toggled and when new groups load.)
  useEffect(() => {
    setFoldableHeight(foldableRef.current?.offsetHeight ?? 0);
  }, [setFoldableHeight, foldableRef, isCollapsed, children]);
  const shouldConstrainHeight = foldableHeight && withFolding && !isFolded;

  return (
    <div
      className={classnames(
        s['root'],
        isCollapsed && s['collapsed'],
        isFolded && s['folded'],
        className
      )}
    >
      <NetworkBarItem
        id={toggleId}
        isCollapsed={isCollapsed}
        icon={<Icon disableDefaultColors size={IconSize.MEDIUM} />}
        tooltipContent={title}
        showTooltip={isFolded}
        biData={biData}
        onClick={withFolding ? onFoldToggle : undefined}
        hasNewContent={isFolded && hasNewContent}
        aria-label={toggleAriaLabel}
        aria-controls={foldableId}
        aria-expanded={!isFolded}
      >
        <div className={s['label']}>
          {title}
          {withFolding && (
            <>
              {' '}
              {isFolded ? (
                <ArrowRight
                  size={IconSize.SMALLER}
                  className={s['arrow']}
                  disableDefaultColors
                />
              ) : (
                <ArrowDown
                  size={IconSize.SMALLER}
                  className={s['arrow']}
                  disableDefaultColors
                />
              )}
            </>
          )}
        </div>
      </NetworkBarItem>
      <div
        className={s['foldable']}
        style={shouldConstrainHeight ? { maxHeight: foldableHeight } : {}}
        id={foldableId}
        aria-labelledby={toggleId}
      >
        <div ref={foldableRef}>{children}</div>
      </div>
    </div>
  );
};
NetworkBarItemGroup.displayName = 'NetworkBarItemGroup';

export default NetworkBarItemGroup;
